// Finance build — one Income Overview breakdown table (by income type/pool,
// by channel, by recipient, or by period). Read-only; each row is a
// recognized-event roll-up with received-of-expected and a variance. The
// variance here is the recognized-event gap, NOT outstanding receivables (a
// never-paid period writes no income event at all).

import {
  Card,
  EmptyState,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type { IncomeBreakdownRowDto } from "@/api/portfolioAdmin/incomeOverviewApi";

interface IncomeBreakdownSectionProps {
  title: string;
  rows: IncomeBreakdownRowDto[];
  /** Heading for the first column (e.g. "Income type / pool", "Channel"). */
  dimensionLabel: string;
  testId: string;
}

export function IncomeBreakdownSection({
  title,
  rows,
  dimensionLabel,
  testId,
}: IncomeBreakdownSectionProps) {
  return (
    <Card data-testid={testId}>
      <h2 className="m-0 mb-space-4 font-display text-h3 font-bold text-onyx">
        {title}
      </h2>
      {rows.length === 0 ? (
        <EmptyState
          title="No income in this window"
          description="No recognized income events fall in the selected range for this breakdown."
          variant="noResults"
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{dimensionLabel}</TH>
              <TH className="text-right">Expected</TH>
              <TH className="text-right">Received</TH>
              <TH className="text-right">Variance</TH>
              <TH className="text-right">Events</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => {
              const variance = row.expected - row.received;
              return (
                <TR key={`${row.group}-${row.key}`} data-testid={`${testId}-row-${row.key}`}>
                  <TD>{row.label}</TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(row.expected)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(row.received)}
                  </TD>
                  <TD
                    className={
                      Math.abs(variance) >= 0.005
                        ? "text-right tabular-nums text-status-error"
                        : "text-right tabular-nums text-gray-mid"
                    }
                  >
                    {formatCurrency(variance)}
                  </TD>
                  <TD className="text-right tabular-nums text-gray-mid">
                    {row.eventCount}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
